import camelCase from "lodash/camelCase";
import mapKeys from "lodash/mapKeys";

import {
  buttonPrimaryDark,
  buttonPrimaryLight,
  buttonSecondaryDark,
  buttonSecondaryLight,
  buttonSmall,
  buttonMedium,
  buttonLarge,
  buttonRound,
  buttonIconOnlySmall,
  buttonIconOnlyMedium,
  buttonIconOnlyLarge,
  buttonSecondaryDarkSelected,
} from "./buttonStyles";
import { inputVariants, selectVariants } from "./inputStyles";
import { checkboxBase, checkboxSmall, checkboxMedium } from "./checkboxStyles";

export const COLORS = {
  BLACK: "#000",
  COOL_BLACK: "#000",
  GREY_1: "#1A1A1A",
  COOL_GREY_1: "#192023",
  GREY_2: "#333",
  COOL_GREY_2: "#31383B",
  GREY_3: "#4D4D4D",
  COOL_GREY_3: "#4A5154",
  GREY_4: "#666",
  COOL_GREY_4: "#62696C",
  GREY_5: "#808080",
  COOL_GREY_5: "#7B8285",
  GREY_6: "#999",
  COOL_GREY_6: "#939A9D",
  GREY_7: "#B3B3B3",
  COOL_GREY_7: "#ACB3B6",
  GREY_8: "#ccc",
  COOL_GREY_8: "#C4CBCE",
  GREY_9: "#E6E6E6",
  COOL_GREY_9: "#DDE4E7",
  WHITE: "#fff",
  COOL_WHITE: "#F5FCFF",

  PRIMARY: "#009CDA",
  PRIMARY_LIGHT: "#29C2FF",
  PRIMARY_DARK: "#00668F",
};

const BREAKPOINT_VALUES = {
  xs: 0,
  sm: 700,
  md: 900,
  lg: 1200,
  xl: 1536,
};

const MOBILE_BREAKPOINT = BREAKPOINT_VALUES.sm;
const TABLET_BREAKPOINT = BREAKPOINT_VALUES.lg;

function createCustomBreakpointAttributes({ containerWidth }) {
  return Object.keys(BREAKPOINT_VALUES).reduce((acc, key) => {
    return {
      ...acc,
      isMobile: containerWidth < MOBILE_BREAKPOINT,
      isTablet:
        containerWidth >= MOBILE_BREAKPOINT &&
        containerWidth < TABLET_BREAKPOINT,
      isDesktop: containerWidth >= TABLET_BREAKPOINT,
      isLessThan: {
        ...(acc.isLessThan || {}),
        [key]: containerWidth < BREAKPOINT_VALUES[key],
      },
      isGreaterThan: {
        ...(acc.isGreaterThan || {}),
        [key]: containerWidth >= BREAKPOINT_VALUES[key],
      },
    };
  }, {});
}

export const createTheme = ({ containerWidth, containerHeight }) => {
  const { isMobile, isTablet, isDesktop, ...customBreakpoints } =
    createCustomBreakpointAttributes({
      containerWidth,
    });

  return {
    isMobile,
    isTablet,
    isDesktop,
    containerWidth,
    containerHeight,
    customBreakpoints,
    space: [0, 4, 8, 16, 32, 64, 128, 256, 512],
    fonts: {
      body: '"akzidenz-grotesk-pro-condens", sans-serif, system-ui',
      heading: '"akzidenz-grotesk-pro-condens", sans-serif, system-ui',
      akzidenzGroteskProCondens: `"akzidenz-grotesk-pro-condens"`,
      roboto: "roboto",
      dinCondensed: "din-condensed",
      diamanteefBoldItalic: "diamanteef-bolditaregular",
      garamondModBauer: "garamond-mod-bauer",
    },
    fontSizes: [12, 14, 16, 20, 24, 32, 48, 64, 96],
    fontWeights: {
      light: 300,
      body: 400,
      heading: 300,
      bold: 700,
    },
    lineHeights: {
      body: 1.5,
      heading: 1.125,
    },
    colors: {
      text: COLORS.BLACK,
      background: COLORS.WHITE,
      primary: COLORS.PRIMARY,
      danger: "#D41D32",
      ...mapKeys(COLORS, (value, key) => camelCase(key)),
    },
    styles: {},

    text: {
      default: {},
      heading: {},

      copy1: {
        fontFamily: "body",
        fontSize: "20px",
        lineHeight: "1.2",
        letterSpacing: "0.025em",
      },

      copy1Light: {
        fontFamily: "body",
        fontSize: "20px",
        lineHeight: "1.2",
        letterSpacing: "0.025em",
        fontWeight: "light",
      },

      copy2: {
        fontFamily: "body",
        fontSize: "16px",
        lineHeight: "1.25",
        letterSpacing: "0.05em",
      },

      copy2Light: {
        fontFamily: "body",
        fontWeight: "light",
        fontSize: "16px",
        lineHeight: "1.25",
        letterSpacing: "0.05em",
      },
      copy2Bold: {
        fontFamily: "body",
        fontWeight: "700",
        fontSize: "16px",
        lineHeight: "1.25",
        letterSpacing: "0.05em",
      },

      copy2Medium: {
        fontFamily: "roboto",
        fontWeight: "300",
        fontSize: "14px",
        lineHeight: "18px",
        letterSpacing: "0.05",
      },

      copy3: {
        fontFamily: "body",
        fontSize: "14px",
        lineHeight: "1.29",
        letterSpacing: "0.05em",
      },

      copy3Light: {
        fontFamily: "roboto",
        fontWeight: "light",
        fontSize: "14px",
        lineHeight: "1.29",
        letterSpacing: "0.05em",
      },

      copy3Medium: {
        fontFamily: "roboto",
        fontWeight: "500",
        fontSize: "14px",
        lineHeight: "1.29",
        letterSpacing: "0.05em",
      },

      copy4Medium: {
        fontFamily: "roboto",
        fontWeight: "500",
        fontSize: "12px",
        lineHeight: "1.33",
        letterSpacing: "0.1em",
      },

      h1: {
        fontFamily: "body",
        fontSize: "60px",
        letterSpacing: "0.01em",
        textTransform: "uppercase",
        fontWeight: "body",
      },

      h2: {
        fontFamily: "body",
        fontSize: "50px",
        letterSpacing: "0.01em",
        textTransform: "uppercase",
        fontWeight: "light",
      },

      h2Light: {
        fontFamily: "body",
        fontSize: "50px",
        letterSpacing: "0.01em",
        textTransform: "uppercase",
        fontWeight: "light",

        strong: {
          fontWeight: "body",
        },
      },

      h3Light: {
        fontFamily: "body",
        fontSize: "40px",
        letterSpacing: "0.01em",
        textTransform: "uppercase",
        fontWeight: "light",
      },

      h3Bold: {
        fontFamily: "body",
        fontSize: "40px",
        letterSpacing: "0.01em",
        textTransform: "uppercase",
        fontWeight: "bold",
      },

      h4Bold: {
        fontFamily: "body",
        fontSize: "30px",
        letterSpacing: "0.025em",
        textTransform: "uppercase",
        fontWeight: "body",
      },

      h4: {
        fontFamily: "body",
        fontSize: "30px",
        letterSpacing: "0.025em",
        textTransform: "uppercase",
        fontWeight: "bold",
      },

      h4Light: {
        fontFamily: "body",
        fontSize: "30px",
        letterSpacing: "0.025em",
        fontWeight: "light",
      },

      h5: {
        fontFamily: "body",
        fontSize: "20px",
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        fontWeight: "body",
      },

      h5Light: {
        fontFamily: "body",
        fontSize: "20px",
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        fontWeight: "light",
      },

      h6: {
        fontFamily: "body",
        fontSize: "16px",
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        fontWeight: "body",
      },

      h6Light: {
        fontFamily: "body",
        fontSize: "16px",
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        fontWeight: "light",
      },
    },

    buttons: {
      unstyled: {
        backgroundColor: "transparent",
        borderRadius: 0,
        cursor: "pointer",
        padding: 0,
      },

      unstyledMediumDark: {
        backgroundColor: "transparent",
        borderRadius: 0,
        cursor: "pointer",
        padding: 0,
      },

      primarySmallLightRound: {
        ...buttonPrimaryLight,
        ...buttonSmall,
        ...buttonRound,
      },
      primarySmallLight: {
        ...buttonPrimaryLight,
        ...buttonSmall,
      },
      primarySmallLightRoundIcon: {
        ...buttonPrimaryLight,
        ...buttonSmall,
        ...buttonRound,
        ...buttonIconOnlySmall,
      },
      primarySmallLightIcon: {
        ...buttonPrimaryLight,
        ...buttonSmall,
        ...buttonIconOnlySmall,
      },
      primarySmallDarkRound: {
        ...buttonPrimaryDark,
        ...buttonSmall,
        ...buttonRound,
      },
      primarySmallDark: {
        ...buttonPrimaryDark,
        ...buttonSmall,
      },
      primarySmallDarkRoundIcon: {
        ...buttonPrimaryDark,
        ...buttonSmall,
        ...buttonRound,
        ...buttonIconOnlySmall,
      },
      primarySmallDarkIcon: {
        ...buttonPrimaryDark,
        ...buttonSmall,
        ...buttonIconOnlySmall,
      },
      primaryMediumLightRound: {
        ...buttonPrimaryLight,
        ...buttonMedium,
        ...buttonRound,
      },
      primaryMediumLight: {
        ...buttonPrimaryLight,
        ...buttonMedium,
      },
      primaryMediumLightRoundIcon: {
        ...buttonPrimaryLight,
        ...buttonMedium,
        ...buttonRound,
        ...buttonIconOnlyMedium,
      },
      primaryMediumLightIcon: {
        ...buttonPrimaryLight,
        ...buttonMedium,
        ...buttonIconOnlyMedium,
      },
      primaryMediumDarkRound: {
        ...buttonPrimaryDark,
        ...buttonMedium,
        ...buttonRound,
      },
      primaryMediumDark: {
        ...buttonPrimaryDark,
        ...buttonMedium,
      },
      primaryMediumDarkRoundIcon: {
        ...buttonPrimaryDark,
        ...buttonMedium,
        ...buttonRound,
        ...buttonIconOnlyMedium,
      },
      primaryMediumDarkIcon: {
        ...buttonPrimaryDark,
        ...buttonMedium,
        ...buttonIconOnlyMedium,
      },
      primaryLargeLightRound: {
        ...buttonPrimaryLight,
        ...buttonLarge,
        ...buttonRound,
      },
      primaryLargeLight: {
        ...buttonPrimaryLight,
        ...buttonLarge,
      },
      primaryLargeLightRoundIcon: {
        ...buttonPrimaryLight,
        ...buttonLarge,
        ...buttonRound,
        ...buttonIconOnlyLarge,
      },
      primaryLargeLightIcon: {
        ...buttonPrimaryLight,
        ...buttonLarge,
        ...buttonIconOnlyLarge,
      },
      primaryLargeDarkRound: {
        ...buttonPrimaryDark,
        ...buttonLarge,
        ...buttonRound,
      },
      primaryLargeDark: {
        ...buttonPrimaryDark,
        ...buttonLarge,
      },
      primaryLargeDarkRoundIcon: {
        ...buttonPrimaryDark,
        ...buttonLarge,
        ...buttonRound,
        ...buttonIconOnlyLarge,
      },
      primaryLargeDarkIcon: {
        ...buttonPrimaryDark,
        ...buttonLarge,
        ...buttonIconOnlyLarge,
      },

      secondarySmallLightRound: {
        ...buttonSecondaryLight,
        ...buttonSmall,
        ...buttonRound,
      },
      secondarySmallLight: {
        ...buttonSecondaryLight,
        ...buttonSmall,
      },
      secondarySmallLightRoundIcon: {
        ...buttonSecondaryLight,
        ...buttonSmall,
        ...buttonRound,
        ...buttonIconOnlySmall,
      },
      secondarySmallLightIcon: {
        ...buttonSecondaryLight,
        ...buttonSmall,
        ...buttonIconOnlySmall,
      },
      secondarySmallDarkRound: {
        ...buttonSecondaryDark,
        ...buttonSmall,
        ...buttonRound,
      },
      secondarySmallDark: {
        ...buttonSecondaryDark,
        ...buttonSmall,
      },
      secondarySmallDarkRoundIcon: {
        ...buttonSecondaryDark,
        ...buttonSmall,
        ...buttonRound,
        ...buttonIconOnlySmall,
      },
      secondarySmallDarkIcon: {
        ...buttonSecondaryDark,
        ...buttonSmall,
        ...buttonIconOnlySmall,
      },
      secondaryMediumLightRound: {
        ...buttonSecondaryLight,
        ...buttonMedium,
        ...buttonRound,
      },
      secondaryMediumLight: {
        ...buttonSecondaryLight,
        ...buttonMedium,
      },
      secondaryMediumLightRoundIcon: {
        ...buttonSecondaryLight,
        ...buttonMedium,
        ...buttonRound,
        ...buttonIconOnlyMedium,
      },
      secondaryMediumLightIcon: {
        ...buttonSecondaryLight,
        ...buttonMedium,
        ...buttonIconOnlyMedium,
      },
      secondaryMediumDarkRound: {
        ...buttonSecondaryDark,
        ...buttonMedium,
        ...buttonRound,
      },
      secondaryMediumDark: {
        ...buttonSecondaryDark,
        ...buttonMedium,
      },
      secondaryMediumDarkRoundIcon: {
        ...buttonSecondaryDark,
        ...buttonMedium,
        ...buttonRound,
        ...buttonIconOnlyMedium,
      },
      secondaryMediumDarkIcon: {
        ...buttonSecondaryDark,
        ...buttonMedium,
        ...buttonIconOnlyMedium,
      },
      secondaryLargeLightRound: {
        ...buttonSecondaryLight,
        ...buttonLarge,
        ...buttonRound,
      },
      secondaryLargeLight: {
        ...buttonSecondaryLight,
        ...buttonLarge,
      },
      secondaryLargeLightRoundIcon: {
        ...buttonSecondaryLight,
        ...buttonLarge,
        ...buttonRound,
        ...buttonIconOnlyLarge,
      },
      secondaryLargeLightIcon: {
        ...buttonSecondaryLight,
        ...buttonLarge,
        ...buttonIconOnlyLarge,
      },
      secondaryLargeDarkRound: {
        ...buttonSecondaryDark,
        ...buttonLarge,
        ...buttonRound,
      },
      secondaryLargeDark: {
        ...buttonSecondaryDark,
        ...buttonLarge,
      },
      secondaryLargeDarkRoundIcon: {
        ...buttonSecondaryDark,
        ...buttonLarge,
        ...buttonRound,
        ...buttonIconOnlyLarge,
      },
      secondaryLargeDarkIcon: {
        ...buttonSecondaryDark,
        ...buttonLarge,
        ...buttonIconOnlyLarge,
      },
      secondaryMediumDarkRoundSelected: {
        ...buttonSecondaryDark,
        ...buttonMedium,
        ...buttonRound,
        ...buttonSecondaryDarkSelected,
      },
      secondaryLargeDarkRoundSelected: {
        ...buttonSecondaryDark,
        ...buttonLarge,
        ...buttonRound,
        ...buttonSecondaryDarkSelected,
      },

      primary: {
        variant: "primaryMediumDark",
      },
    },

    forms: {
      ...inputVariants,
      input: {
        variant: "inputRegularMedium",
      },

      ...selectVariants,
      select: {
        variant: "selectRegularMedium",
      },

      // <Checkbox />
      checkboxSmall: {
        ...checkboxBase,
        ...checkboxSmall,
      },
      checkboxMedium: {
        ...checkboxBase,
        ...checkboxMedium,
      },
      checkbox: {
        variant: "checkboxMedium",
      },
    },
  };
};
